import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Phone, Send, ChevronDown, ChevronUp, MapPin, Star, Shield, Clock, Users, CheckCircle2, Wrench, PaintBucket, Home, Hammer, BrickWall, Camera, X, Loader2 } from 'lucide-react';
import { projects, testimonials } from '../data/mock';
import { cityConfigs } from '../data/cityConfigs';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PHONE = '06.43.97.17.21';
const PHONE_TEL = 'tel:+33643971721';

/* ─── Google Ads Conversion Tracking ─── */
// PLACEHOLDER: Replace AW-XXXXXXXXX with your actual Google Ads conversion ID
// Replace CONVERSION_LABEL with your actual conversion label from Google Ads
const GADS_CONVERSION_ID = 'AW-XXXXXXXXX';
const GADS_PHONE_LABEL = 'PHONE_CONVERSION_LABEL';
const GADS_FORM_LABEL = 'FORM_CONVERSION_LABEL';

const track = (event, label) => {
  if (window.gtag) {
    window.gtag('event', event, { event_category: 'landing', event_label: label });
  }
};

const trackConversion = (label) => {
  if (window.gtag && GADS_CONVERSION_ID !== 'AW-XXXXXXXXX') {
    window.gtag('event', 'conversion', {
      send_to: `${GADS_CONVERSION_ID}/${label}`,
      value: 1.0,
      currency: 'EUR',
    });
  }
  // Always track in GA too
  track('conversion', label);
};

/* ─── Static data ─── */
const servicesList = [
  { icon: PaintBucket, title: 'Ravalement de façade', desc: 'Nettoyage, réparation et remise en état complète de vos façades.' },
  { icon: Home, title: 'Rénovation de façade', desc: "Transformation et modernisation de l'apparence de votre habitat." },
  { icon: BrickWall, title: 'Peinture de façade', desc: 'Application de peintures et enduits décoratifs haute tenue.' },
  { icon: Wrench, title: 'Réparation des supports', desc: 'Traitement des fissures, reprises de maçonnerie et consolidation.' },
  { icon: Hammer, title: 'Rejointoiement', desc: 'Réfection des joints de briques et pierres pour une façade saine.' },
  { icon: Shield, title: 'Isolation thermique par l\'extérieur', desc: 'ITE performante pour réduire vos factures et améliorer votre confort.' },
];

const faqData = [
  { q: 'Combien coûte un ravalement de façade ?', a: 'Le coût dépend de la surface, de l\'état de la façade et des travaux nécessaires. Nous proposons un devis gratuit et détaillé après visite sur site.' },
  { q: 'Comment obtenir un devis ?', a: 'Remplissez le formulaire sur cette page ou appelez-nous au 06.43.97.17.21. Nous organisons une visite gratuite pour étudier votre projet.' },
  { q: 'Dans quelles villes intervenez-vous ?', a: 'Nous intervenons dans toute la région Hauts-de-France : Somme, Oise, Aisne, Nord et Pas-de-Calais.' },
  { q: 'Quels types de façades rénovez-vous ?', a: 'Nous intervenons sur tous types de supports : brique, pierre, béton, enduit, parpaing. Chaque façade fait l\'objet d\'un diagnostic adapté.' },
  { q: 'Combien de temps durent les travaux ?', a: 'La durée dépend de la taille du chantier. En moyenne, comptez 2 à 4 semaines pour une maison individuelle.' },
  { q: 'Quelles aides financières sont disponibles ?', a: 'Pour l\'isolation thermique par l\'extérieur, vous pouvez bénéficier de MaPrimeRénov\', des CEE et d\'autres aides selon vos revenus.' },
  { q: 'Intervenez-vous toute l\'année ?', a: 'Oui, nous adaptons nos méthodes selon les conditions météorologiques et vous conseillons sur le meilleur moment pour votre projet.' },
];

const departments = [
  { name: 'Somme (80)', cities: 'Amiens, Abbeville, Péronne, Albert, Montdidier' },
  { name: 'Oise (60)', cities: 'Beauvais, Compiègne, Senlis, Creil, Clermont' },
  { name: 'Aisne (02)', cities: 'Laon, Saint-Quentin, Soissons, Château-Thierry' },
  { name: 'Nord (59)', cities: 'Lille, Valenciennes, Douai, Cambrai, Maubeuge' },
  { name: 'Pas-de-Calais (62)', cities: 'Arras, Calais, Lens, Boulogne-sur-Mer' },
];

const typeOptions = [
  'Ravalement de façade', 'Rénovation de façade', 'Peinture de façade',
  'Isolation thermique par l\'extérieur', 'Réparation / Entretien', 'Rejointoiement', 'Autre',
];

/* ─── FAQ Item ─── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-4 text-left text-gray-800 font-medium text-sm md:text-base">
        <span>{q}</span>
        {open ? <ChevronUp size={20} className="shrink-0 text-cyan-600" /> : <ChevronDown size={20} className="shrink-0 text-gray-400" />}
      </button>
      {open && <p className="pb-4 text-gray-600 text-sm leading-relaxed">{a}</p>}
    </div>
  );
}

/* ─── Main Landing Page ─── */
export default function LandingAds() {
  const { slug } = useParams();
  const config = cityConfigs[slug] || cityConfigs['hauts-de-france'];

  const formRef = useRef(null);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({ name: '', phone: '', ville: config.defaultVille, type_travaux: '', message: '' });
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState('');

  useEffect(() => {
    document.title = config.metaTitle;
  }, [config.metaTitle]);

  const scrollToForm = () => {
    track('cta_click', 'scroll_to_form');
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePhoneClick = () => {
    trackConversion(GADS_PHONE_LABEL);
    track('conversion', 'phone_click');
  };

  /* ─── Photo handlers ─── */
  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const newPhotos = files.slice(0, 5 - photos.length).map((f) => ({
      file: f,
      preview: URL.createObjectURL(f),
      name: f.name,
    }));
    setPhotos((prev) => [...prev, ...newPhotos].slice(0, 5));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  /* ─── Form submit ─── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;
    setSending(true);
    setError('');

    try {
      // Upload photos first
      const photoUrls = [];
      if (photos.length > 0) {
        setUploadProgress(`Upload des photos (0/${photos.length})...`);
        for (let i = 0; i < photos.length; i++) {
          setUploadProgress(`Upload des photos (${i + 1}/${photos.length})...`);
          const fd = new FormData();
          fd.append('file', photos[i].file);
          const upRes = await fetch(`${API_URL}/api/upload`, { method: 'POST', body: fd });
          if (upRes.ok) {
            const upData = await upRes.json();
            photoUrls.push(upData.path);
          }
        }
      }

      setUploadProgress('Envoi de votre demande...');
      const res = await fetch(`${API_URL}/api/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          photo_urls: photoUrls,
          source: `landing-google-ads-${slug || 'hauts-de-france'}`,
        }),
      });
      if (!res.ok) throw new Error();
      setSent(true);
      trackConversion(GADS_FORM_LABEL);
      track('generate_lead', 'form_submit');
    } catch {
      setError('Une erreur est survenue. Veuillez nous appeler directement.');
    } finally {
      setSending(false);
      setUploadProgress('');
    }
  };

  const selectedProjects = projects.slice(0, 6);

  return (
    <div className="min-h-screen bg-white text-gray-800">
      {/* ─── MINI HEADER ─── */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <span className="font-bold text-lg text-gray-900 tracking-tight">
            LES FAÇADIERS <span className="text-cyan-600">HDF</span>
          </span>
          <a href={PHONE_TEL} onClick={handlePhoneClick} data-testid="header-phone-btn"
            className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors">
            <Phone size={16} />
            <span className="hidden sm:inline">{PHONE}</span>
            <span className="sm:hidden">Appeler</span>
          </a>
        </div>
      </header>

      {/* ─── 1. HERO ─── */}
      <section className="relative bg-cover bg-center" style={{ backgroundImage: `url(${projects[0].images[0]})` }}>
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/85 via-gray-900/75 to-cyan-900/70" />
        <div className="relative max-w-5xl mx-auto px-4 py-16 md:py-24 text-white">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-4" data-testid="hero-h1">
            {config.h1Line1}<br />{config.h1Line2}
          </h1>
          <p className="text-base sm:text-lg text-gray-200 max-w-xl mb-8 leading-relaxed">{config.subtitle}</p>

          <div className="flex flex-col sm:flex-row gap-3 mb-10">
            <a href={PHONE_TEL} onClick={handlePhoneClick} data-testid="hero-phone-btn"
              className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 font-bold text-base px-6 py-3.5 rounded-full hover:bg-gray-100 transition-colors shadow-lg">
              <Phone size={20} className="text-cyan-600" /> Appeler maintenant
            </a>
            <button onClick={scrollToForm} data-testid="hero-devis-btn"
              className="inline-flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-base px-6 py-3.5 rounded-full transition-colors shadow-lg">
              <Send size={18} /> Demander un devis gratuit
            </button>
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-300">
            {['Devis gratuit', 'Entreprise spécialisée en façade', 'Assurance décennale', 'Intervention locale'].map((t) => (
              <span key={t} className="flex items-center gap-1.5"><CheckCircle2 size={16} className="text-cyan-400" />{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 2. PREUVE SOCIALE ─── */}
      <section className="bg-gray-50 py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-center mb-2">Ils nous font confiance</h2>
          <div className="flex items-center justify-center gap-1 mb-8">
            {[...Array(5)].map((_, i) => <Star key={i} size={20} className="fill-amber-400 text-amber-400" />)}
            <span className="ml-2 text-sm text-gray-500">5/5 — Avis vérifiés</span>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {testimonials.map((t) => (
              <div key={t.id} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex gap-0.5">
                    {[...Array(t.rating)].map((_, i) => <Star key={i} size={14} className="fill-amber-400 text-amber-400" />)}
                  </div>
                  <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">{t.source}</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed mb-3 flex-1">« {t.text} »</p>
                <p className="text-xs font-semibold text-gray-500">{t.name}</p>
              </div>
            ))}
          </div>
          <p className="text-center mt-6">
            <a href="https://www.google.com/search?q=les+fa%C3%A7adiers+hdf+avis" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline text-sm font-medium">
              Voir nos avis Google &rarr;
            </a>
          </p>
        </div>
      </section>

      {/* ─── CTA INTERMÉDIAIRE ─── */}
      <section className="bg-cyan-600 py-8">
        <div className="max-w-5xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white font-semibold text-base md:text-lg text-center sm:text-left">Vous préférez en parler directement ?</p>
          <a href={PHONE_TEL} onClick={handlePhoneClick} data-testid="mid-phone-btn"
            className="inline-flex items-center gap-2 bg-white text-cyan-700 font-bold px-6 py-3 rounded-full hover:bg-gray-100 transition-colors shadow">
            <Phone size={18} /> {PHONE}
          </a>
        </div>
      </section>

      {/* ─── 3. RÉALISATIONS ─── */}
      <section className="py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-center mb-2">Nos réalisations</h2>
          <p className="text-gray-500 text-sm text-center mb-8">Découvrez quelques-uns de nos chantiers en Hauts-de-France</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {selectedProjects.map((p) => (
              <div key={p.id} className="group rounded-xl overflow-hidden shadow-sm border border-gray-100 bg-white">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.images[0]} alt={`Réalisation ${p.location}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-1.5 text-cyan-600 text-xs font-medium mb-1">
                    <MapPin size={13} /> {p.location}
                  </div>
                  <p className="text-sm font-semibold text-gray-800">{p.type}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 4. SERVICES ─── */}
      <section className="bg-gray-50 py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-center mb-2">Nos travaux de façade</h2>
          <p className="text-gray-500 text-sm text-center mb-8">Des prestations adaptées à chaque projet</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {servicesList.map((s, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center mb-3">
                  <s.icon size={20} className="text-cyan-600" />
                </div>
                <h3 className="font-semibold text-gray-800 text-sm mb-1">{s.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 5. POURQUOI NOUS ─── */}
      <section className="py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-center mb-8">Pourquoi nous contacter ?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Users, title: 'Un interlocuteur pour votre projet', desc: 'Un seul contact du début à la fin de vos travaux.' },
              { icon: CheckCircle2, title: 'Étude de votre façade', desc: 'Visite sur site et diagnostic complet avant intervention.' },
              { icon: Send, title: 'Devis gratuit', desc: 'Estimation détaillée et transparente, sans engagement.' },
              { icon: Clock, title: 'Plus de 10 ans d\'expérience', desc: 'Des professionnels qualifiés au service de votre façade.' },
            ].map((a, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-full bg-cyan-50 flex items-center justify-center mx-auto mb-3">
                  <a.icon size={22} className="text-cyan-600" />
                </div>
                <h3 className="font-semibold text-gray-800 text-sm mb-1">{a.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 6. ZONE D'INTERVENTION ─── */}
      <section className="bg-gray-50 py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-center mb-2">Nous intervenons dans les Hauts-de-France</h2>
          <p className="text-gray-500 text-sm text-center mb-8">5 départements couverts pour vos travaux de façade</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {departments.map((d, i) => (
              <div key={i} className={`rounded-xl p-4 border shadow-sm ${
                config.highlightDept === d.name
                  ? 'bg-cyan-50 border-cyan-300 ring-2 ring-cyan-200'
                  : 'bg-white border-gray-100'
              }`}>
                <h3 className="font-semibold text-cyan-700 text-sm mb-1">{d.name}</h3>
                <p className="text-gray-500 text-xs">{d.cities}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 7. FORMULAIRE ─── */}
      <section ref={formRef} className="py-12 md:py-16" id="devis">
        <div className="max-w-xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-center mb-2">Vous avez un projet de façade ?</h2>
          <p className="text-gray-500 text-sm text-center mb-8">Décrivez-nous votre projet et recevez votre devis.</p>

          {sent ? (
            <div data-testid="form-success" className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
              <CheckCircle2 size={48} className="text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-gray-800 mb-2">Demande envoyée !</h3>
              <p className="text-sm text-gray-600 mb-4">Nous vous recontactons dans les plus brefs délais.</p>
              <a href={PHONE_TEL} onClick={handlePhoneClick}
                className="inline-flex items-center gap-2 text-cyan-600 font-semibold text-sm hover:underline">
                <Phone size={16} /> Ou appelez-nous : {PHONE}
              </a>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-form">
              <input data-testid="lead-name" type="text" placeholder="Votre nom *" required
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500" />
              <input data-testid="lead-phone" type="tel" placeholder="Votre téléphone *" required
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500" />
              <input data-testid="lead-ville" type="text" placeholder="Votre ville"
                value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500" />
              <select data-testid="lead-type" value={form.type_travaux}
                onChange={(e) => setForm({ ...form, type_travaux: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 bg-white">
                <option value="">Type de travaux</option>
                {typeOptions.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              <textarea data-testid="lead-message" rows={3} placeholder="Décrivez votre projet..."
                value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 resize-none" />

              {/* ─── PHOTO UPLOAD ─── */}
              <div>
                <label className="text-sm text-gray-600 mb-2 block">Ajouter des photos de votre façade (max 5)</label>
                {photos.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {photos.map((p, i) => (
                      <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                        <img src={p.preview} alt={p.name} className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removePhoto(i)}
                          className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center">
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {photos.length < 5 && (
                  <button type="button" onClick={() => fileInputRef.current?.click()} data-testid="photo-upload-btn"
                    className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700 font-medium py-2 px-4 border border-dashed border-cyan-300 rounded-lg hover:bg-cyan-50 transition-colors">
                    <Camera size={18} /> Ajouter des photos
                  </button>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handlePhotoSelect}
                  className="hidden" data-testid="photo-file-input" />
              </div>

              {error && <p className="text-red-500 text-sm">{error}</p>}
              <button type="submit" disabled={sending} data-testid="lead-submit-btn"
                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-3.5 rounded-lg text-base transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {sending ? (<><Loader2 size={18} className="animate-spin" /> {uploadProgress || 'Envoi en cours...'}</>) : 'DEMANDER MON DEVIS GRATUIT'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ─── 8. FAQ ─── */}
      <section className="bg-gray-50 py-12 md:py-16">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-center mb-8">Questions fréquentes</h2>
          <div>{faqData.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}</div>
        </div>
      </section>

      {/* ─── 9. DERNIER CTA ─── */}
      <section className="bg-gray-900 py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-3">Prêt à rénover votre façade ?</h2>
          <p className="text-gray-400 text-sm mb-6">Obtenez votre devis gratuit ou appelez-nous directement.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href={PHONE_TEL} onClick={handlePhoneClick} data-testid="footer-phone-btn"
              className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 font-bold px-6 py-3.5 rounded-full hover:bg-gray-100 transition-colors">
              <Phone size={18} className="text-cyan-600" /> {PHONE}
            </a>
            <button onClick={scrollToForm} data-testid="footer-devis-btn"
              className="inline-flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white font-bold px-6 py-3.5 rounded-full transition-colors">
              Demander un devis gratuit
            </button>
          </div>
        </div>
      </section>

      {/* ─── 10. FOOTER ─── */}
      <footer className="bg-gray-950 text-gray-500 py-6 pb-20 md:pb-6">
        <div className="max-w-5xl mx-auto px-4 text-center text-xs">
          <p>&copy; {new Date().getFullYear()} Les Façadiers HDF — Ravalement de façade {config.cityName !== 'Hauts-de-France' ? `à ${config.cityName} et ` : ''}dans les Hauts-de-France</p>
          <p className="mt-1">{PHONE} — facadiershdf@gmail.com</p>
        </div>
      </footer>

      {/* ─── 11. BARRE FIXE MOBILE ─── */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden flex border-t border-gray-200 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.1)]">
        <a href={PHONE_TEL} onClick={handlePhoneClick} data-testid="sticky-phone-btn"
          className="flex-1 flex items-center justify-center gap-2 py-3.5 text-cyan-700 font-bold text-sm border-r border-gray-200">
          <Phone size={18} /> Appeler
        </a>
        <button onClick={scrollToForm} data-testid="sticky-devis-btn"
          className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-cyan-600 text-white font-bold text-sm">
          <Send size={16} /> Devis gratuit
        </button>
      </div>
    </div>
  );
}
